import { useMemo } from 'react';
import { useTRPCAPI, useTRPCQuery } from '../../../../trpc/trpc';
import type { NewsletterState } from '../../../../../../music24-api/src/app/services/songsbrew/songsBrewSubscription';

export type SongsBrewState = NewsletterState;
export type SongsBrewStatus = NewsletterState['status'];

interface Options {
  /** Settings reads the platform every time (`fresh: true`); Home and the welcome do not. */
  fresh?: boolean;
  enabled?: boolean;
  /** Home reads the platform again on each mount, so a change made elsewhere shows. */
  refetchOnMount?: 'always';
}

/** The "Send us your track" page, for an answer with no address (the server always sends one). */
export const SEND_TRACK_FALLBACK_URL = 'https://songsbrew.com/submit';

const STATUSES: readonly SongsBrewStatus[] = ['none', 'subscribed', 'unsubscribed', 'refused'];

/**
 * The answer as the clients use it: null while it loads, when the call fails, when an old server
 * has no `newsletter` router (NOT_FOUND), when the platform is not set up (`available: false`), or
 * when the answer has an unknown shape. Every field gets a default, for an older server.
 */
export function songsBrewStateOf(data: unknown): SongsBrewState | null {
  if (typeof data !== 'object' || data === null) {
    return null;
  }
  const answer = data as Partial<NewsletterState>;
  if (answer.available !== true) {
    return null;
  }
  const status = STATUSES.find((value) => value === answer.status) ?? 'none';
  return {
    available: true,
    status,
    cardVisible: answer.cardVisible === true,
    sendTrackUrl:
      typeof answer.sendTrackUrl === 'string' && answer.sendTrackUrl !== ''
        ? answer.sendTrackUrl
        : SEND_TRACK_FALLBACK_URL,
  };
}

/**
 * Reads `newsletter.getState` (design: songsbrew-promotion README, "Calls and states per placement").
 * The state keeps its identity while the answer does not change, so an effect on it runs only for
 * a new answer. An error hides the state only when there is no answer: a failed refetch (a focus
 * blip) keeps the last good answer.
 */
export function useSongsBrewState({ fresh = false, enabled = true, refetchOnMount }: Options = {}): {
  state: SongsBrewState | null;
  isLoading: boolean;
  /** True when an answer came from a read of this mount, not only from the cache. */
  isFetchedAfterMount: boolean;
} {
  const trpcAPI = useTRPCAPI();
  const query = useTRPCQuery(
    trpcAPI.newsletter.getState.queryOptions(fresh ? { fresh: true } : undefined, {
      retry: false,
      enabled,
      ...(refetchOnMount === undefined ? {} : { refetchOnMount }),
    })
  );
  const failedWithNoAnswer = query.error !== null && query.error !== undefined && query.data === undefined;
  const state = useMemo(
    () => (failedWithNoAnswer ? null : songsBrewStateOf(query.data)),
    [failedWithNoAnswer, query.data]
  );
  return {
    state,
    isLoading: enabled && query.isLoading === true,
    isFetchedAfterMount: query.isFetchedAfterMount === true,
  };
}
